import { useIntl } from 'react-intl';
import styles from './index.module.scss';
import AccionaButton from '../../../../../components/AccionaButton';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Marker, Overlay, Map as PigeonMap } from 'pigeon-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getCemexTracking } from '../../../../../services/api/cemexTracking';
import dayjs from 'dayjs';
import Spinner from '../../../../../components/Spinner';
import { ReactComponent as ConstructionPlaceMarker } from '../../../../../style/img/acciona-leaf-marker.svg';
import { ReactComponent as TruckMarker } from '../../../../../style/img/truck-marker.svg';
import { Descriptions, Flex, message, Popover, Segmented, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getCemexTrackingOrders } from '../../../../../services/redux/cemexTracking/actions';
import { STATUS_DICTIONARY } from '../List';
import { ReactComponent as Truck } from '../../../../../style/img/cemex-tracking-grey.svg';

const { title, actionsWrapper, label } = styles;

const Map = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { project } = useOutletContext();
  const [trackingInfo, setTrackingInfo] = useState([]);
  const [mapCenter, setMapCenter] = useState([40.4168, -3.7038]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [segmentedOption, setSegmentedOption] = useState('all');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(false);
  const orders = useSelector(({ cemexTracking }) => cemexTracking.list);
  const interval = useRef();
  const mapTiler = (x, y, z, dpr) => {
    return `https://a.basemaps.cartocdn.com/light_all/${z}/${x}/${y}${dpr}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`;
  };

  const getTrackingUpdate = useCallback(async () => {
    const tracking = await getCemexTracking({
      filters: {
        date: dayjs().format('YYYY-MM-DD'),
        project: project.id_project,
      },
    });
    const firstTracking = tracking.data.orders?.find(
      (o) => o.trackings?.length > 0,
    )?.trackings[0];
    if (firstTracking) {
      setTrackingInfo(tracking.data.orders);
      setError(false);
      setMapCenter([
        parseFloat(firstTracking.JSLatitude),
        parseFloat(firstTracking.JSLongitude),
      ]);
      setUpdating(false);
    } else {
      setTrackingInfo([]);
      setError(true);
      setUpdating(false);
      message.error(
        intl.formatMessage({
          id: 'cemexTracking.map.error.message',
        }),
      );
      clearInterval(interval.current);
    }
  }, [intl, project.id_project]);

  useEffect(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }

    if (!error) {
      interval.current = setInterval(
        () => getTrackingUpdate(),
        process.env.REACT_APP_UPDATE_CEMEX_TRACKING_MAP_INTERVAL_IN_MILLIS ||
          5000,
      );
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [error, getTrackingUpdate, intl]);

  const getFilteredTrackingInfo = useCallback(
    (option) => {
      return trackingInfo
        .map((order) => {
          const filteredTrackings = order.trackings.filter((tracking) => {
            if (option === 'inRoute')
              return !['POU', 'OST'].includes(tracking.truckStatusGroupCode);
            if (option === 'inPlace')
              return ['POU', 'OST'].includes(tracking.truckStatusGroupCode);
            return true; // 'all'
          });

          return {
            ...order,
            trackings: filteredTrackings,
          };
        })
        .filter((order) => order.trackings.length > 0); // elimina pedidos sin trackings relevantes
    },
    [trackingInfo],
  );

  useEffect(() => {
    dispatch(
      getCemexTrackingOrders({
        filters: {
          date: dayjs().format('YYYY-MM-DD'),
          project: project.id_project,
        },
      }),
    );
  }, [dispatch, project.id_project]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div
          className="col-sm-12"
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <AccionaButton
            id="viewList"
            type="white"
            onClick={() => {
              navigate('../list');
            }}
            msg={intl.formatMessage({ id: 'button.list' })}
            classIcon="icon fas fa-list"
          />
          <AccionaButton
            id="reoladMap"
            type="white"
            onClick={() => {
              setError(false);
              setUpdating(true);
              dispatch(
                getCemexTrackingOrders({
                  filters: {
                    date: dayjs().format('YYYY-MM-DD'),
                    project: project.id_project,
                  },
                }),
              );
              getTrackingUpdate();
            }}
            msg={intl.formatMessage({ id: 'button.update.end' })}
            classIcon="icon fas fa-sync"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className={title}>
            {intl.formatMessage({ id: 'cemexTracking.map.title' })}
          </div>
        </div>
        <div className="col-sm-12">
          {error && (
            <div
              style={{
                height: '60vh',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#ececec',
                flexDirection: 'column',
                gap: '10px',
                color: '#666666',
              }}
            >
              <Truck width="80px" height="80px" />
              <p>
                {intl.formatMessage({
                  id: 'cemexTracking.map.no.trucks.incoming',
                })}
              </p>
            </div>
          )}
          {!error && (
            <div style={{ height: '60vh', width: '100%' }}>
              {(trackingInfo?.length === 0 || updating) && (
                <Spinner
                  loading={true}
                  style={{
                    width: 'calc(100% - 30px)',
                    position: 'absolute',
                    zIndex: 1,
                    height: '60vh',
                    minHeight: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  }}
                />
              )}
              <PigeonMap
                defaultZoom={13}
                provider={mapTiler}
                zoom={14}
                defaultCenter={mapCenter}
                center={mapCenter}
                height="60vh"
              >
                <Segmented
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1,
                  }}
                  options={[
                    {
                      value: 'inRoute',
                      label: intl
                        .formatMessage({
                          id: 'cemexTracking.map.inRoute',
                        })
                        .toLocaleUpperCase(),
                      className: label,
                    },
                    {
                      value: 'inPlace',
                      label: intl
                        .formatMessage({
                          id: 'cemexTracking.map.inPlace',
                        })
                        .toLocaleUpperCase(),
                      className: label,
                    },
                    {
                      value: 'all',
                      label: intl
                        .formatMessage({
                          id: 'cemexTracking.map.all',
                        })
                        .toLocaleUpperCase(),
                      className: label,
                    },
                  ]}
                  value={segmentedOption}
                  onChange={setSegmentedOption}
                />
                {selectedTruck && (
                  <Overlay
                    anchor={[
                      parseFloat(selectedTruck.latitude),
                      parseFloat(selectedTruck.longitude),
                    ]}
                    offset={[0, 0]}
                  >
                    <Popover title="heyy" />
                  </Overlay>
                )}
                {trackingInfo?.length !== 0 && (
                  <Marker anchor={mapCenter}>
                    <ConstructionPlaceMarker />
                  </Marker>
                )}
                {getFilteredTrackingInfo(segmentedOption).map((order) => {
                  return order.trackings.map((tracking) => {
                    const ticket = orders
                      .find((o) => o.orderId === order.orderId)
                      ?.tickets?.find(
                        (ticket) =>
                          ticket.ticketSourceCode === tracking.ticketSourceCode,
                      );
                    return (
                      <Marker
                        anchor={[
                          parseFloat(tracking.latitude),
                          parseFloat(tracking.longitude),
                        ]}
                        key={tracking.truckId}
                        onClick={() => {
                          setSelectedTruck(tracking);
                        }}
                      >
                        <Popover
                          overlayStyle={{ maxWidth: '350px' }}
                          content={
                            <Flex>
                              <Descriptions
                                width="300px"
                                column={1}
                                labelStyle={{
                                  color: '#000000',
                                  textTransform: 'uppercase',
                                }}
                              >
                                <Descriptions.Item
                                  label={intl.formatMessage({
                                    id: 'cemexTracking.map.marker.load',
                                  })}
                                >
                                  {tracking.loadNumber}
                                </Descriptions.Item>
                                <Descriptions.Item
                                  label={intl.formatMessage({
                                    id: 'cemexTracking.map.marker.docketNumber',
                                  })}
                                >
                                  {tracking.ticketSourceCode}
                                </Descriptions.Item>
                                <Descriptions.Item
                                  label={intl.formatMessage({
                                    id: 'cemexTracking.map.marker.truckPlate',
                                  })}
                                >
                                  {tracking.truckPlate}
                                </Descriptions.Item>
                                <Descriptions.Item
                                  label={intl.formatMessage({
                                    id: 'cemexTracking.map.marker.product',
                                  })}
                                >
                                  {ticket?.productDesc}
                                </Descriptions.Item>
                              </Descriptions>
                              <Flex vertical align="flex-end">
                                <Tag
                                  style={{ marginInlineEnd: '0px' }}
                                  color={
                                    STATUS_DICTIONARY[
                                      tracking.truckStatusGroupCode
                                    ].color
                                  }
                                >
                                  {intl
                                    .formatMessage({
                                      id:
                                        'cemex.status.' +
                                        tracking.truckStatusGroupCode,
                                    })
                                    .toLocaleUpperCase()}
                                </Tag>
                                <p>
                                  {ticket?.totalDeliveredQuantity +
                                    ' ' +
                                    (ticket?.unitOfMeasure || 'M3')}
                                </p>
                              </Flex>
                            </Flex>
                          }
                        >
                          <TruckMarker style={{ pointerEvents: 'auto' }} />
                        </Popover>
                      </Marker>
                    );
                  });
                })}
              </PigeonMap>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Map;
